import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, formatError, extractToken } from "@/lib/api";
import { normalizeUser } from "@/lib/adapters";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out, object = logged in
  const [ready, setReady] = useState(false);
  const queryClient = useQueryClient();

  const load = useCallback(async () => {
    const token = localStorage.getItem("bd_token");
    if (!token) { setUser(false); setReady(true); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(normalizeUser(data));
    } catch {
      localStorage.removeItem("bd_token");
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const authRequest = async (path, payload) => {
    try {
      const { data } = await api.post(path, payload);
      const token = extractToken(data);
      // A different account may be signing in — drop all cached data (e.g.
      // admin flags) from any previous session.
      queryClient.clear();
      if (token) localStorage.setItem("bd_token", token);
      const u = normalizeUser(data);
      if (token && (!u || !u.email)) {
        const me = await api.get("/auth/me");
        setUser(normalizeUser(me.data));
      } else {
        setUser(u);
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const login = (email, password) => authRequest("/auth/login", { email, password });
  const register = (payload) => authRequest("/auth/register", payload);
  // Emergent-managed Google sign-in: the mobile backend swaps the short-lived
  // Emergent session_id (from the OAuth redirect fragment) for a Better Days
  // session, upserting the user by their Google email.
  const loginWithGoogleSession = (sessionId) => authRequest("/auth/session", { session_id: sessionId });

  const logout = () => {
    localStorage.removeItem("bd_token");
    queryClient.clear();
    setUser(false);
  };

  const patchProfile = async (payload) => {
    const { data } = await api.patch("/auth/me", payload);
    const u = normalizeUser(data);
    setUser(u);
    return u;
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, register, loginWithGoogleSession, logout, patchProfile, reload: load }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
